import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/card.js';
import { PageHeader } from '../components/page-header.js';
import { useEmployee } from '../team/team-api.js';

const NOT_A_PERSON = new ApiProblem(404, 'No encontrada', 'NOT_FOUND', undefined, undefined, []);

/**
 * Ficha de un subordinado (W5): `GET /employees/{id}`. Muestra nombre, correo, cargo y si está activo; nunca
 * `entraObjectId`, `upn`, `areaId` ni `managerId`. Si ya no es subordinado directo, la API responde 404.
 * Con el aspecto del detalle propio: cabecera con el nombre y los datos en una tarjeta, de solo lectura.
 */
export function TeamMemberPage() {
  const { id = '' } = useParams();
  if (!z.uuid().safeParse(id).success) return <ApiErrorView error={NOT_A_PERSON} />;
  return <TeamMember id={id} />;
}

function TeamMember({ id }: { id: string }) {
  const employee = useEmployee(id);
  if (employee.isPending) return <output className="block text-muted-foreground">Cargando…</output>;
  if (employee.isError) return <ApiErrorView error={employee.error} />;
  const person = employee.data;
  const rows: [string, string][] = [
    ['Correo', person.email],
    ['Cargo', person.jobTitle ?? '—'],
    ['Estado', person.active ? 'Activo' : 'Inactivo'],
  ];
  return (
    <div className="space-y-6 lg:px-6 lg:py-4">
      <Link to="/mi-equipo" className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
        Volver a mi equipo
      </Link>
      <PageHeader title={person.fullName} description="Datos de una persona de tu equipo." />
      <Card>
        <CardHeader>
          <CardTitle>Datos</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-[max-content_1fr]">
            {rows.map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="text-sm text-muted-foreground">{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
